import * as Effects from "redux-saga/effects";

import * as UserAction from "../actions/userAction";
import * as GrainTypeAction from "../actions/grainTypeAction";
import * as api from "../../api";
import { showErrorMessage } from "./shared";
import { IStore } from "../reducers";
import { select } from "redux-saga/effects";
import { IGrainWithProfiles, IUserProfile } from "src/models";
import { getCacheExpiryDuration } from "src/utils/remoteConfig";
import { isElectron } from "src/utils/miscUtils";
import { logDesktopAuthEvent } from "src/utils/desktopLog";
import { extractProfileSettingValue } from "src/utils/profileSettingSync";
import { Auth as Authorization } from 'aws-amplify';

const call: any = Effects.call;
const put: any = Effects.put;
const takeEvery: any = Effects.takeEvery;
const all: any = Effects.all;
const fork: any = Effects.fork;

export const getUserGroups = (store: IStore) => store.user.userProfile.response?.groups;
export const getExpiry = (store: IStore) => store.user.userProfile.expiry;
export const getCachedUserProfile = (state: IStore) => state.user.userProfile.response;
export const getGrainId = (state: any) => state.grainType.grainId;
export const getNetworkStatus = (store: IStore) => store.app.isOnline;

const getBrowserOnline = (): boolean | undefined => {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean') {
      return navigator.onLine;
    }
  } catch {
    // ignore
  }
  return undefined;
};

const getAuthenticatedUsername = async (): Promise<string | undefined> => {
  try {
    const user = await Authorization.currentUserInfo();
    return user?.username || user?.attributes?.preferred_username || user?.attributes?.email;
  } catch {
    return undefined;
  }
};

export function* logUserActivity(action: ReturnType<typeof UserAction.logUserActivity.request>): any {
  try {
    const response = yield call(api.logUserActivity, action.payload);

    yield put(UserAction.logUserActivity.success(response));
  } catch (error: any) {
    showErrorMessage(error);
    yield put(UserAction.logUserActivity.failure(error));
  }
}

export function* getUserSettings(action: ReturnType<typeof UserAction.getUserSettings.request>): any {
  try {
    const response = yield call(api.getUserSettings, action.payload);
    const key = `${action.payload.orgId || ''}:${action.payload.grainId || ''}:${action.payload.profileId || ''}:${action.payload.settingName || ''}`;
    const value = extractProfileSettingValue({
      response,
      settingName: action.payload.settingName,
      grainId: action.payload.grainId,
      profileId: action.payload.profileId
    });

    yield put(UserAction.getUserSettings.success({
      key,
      value
    }));
  } catch (error: any) {
    showErrorMessage(error);
  }
}

export function* getUserInfoFields(action: ReturnType<typeof UserAction.getUserInfoFields.request>): any {
  try {
    const response = yield call(api.getUserInfoFields, action.payload);

    const key = `${action.payload.orgId || ''}:${action.payload.grainId || ''}:${action.payload.profileId || ''}:${action.payload.settingName || ''}`;
    yield put(UserAction.getUserInfoFields.success({ key, fields: response }));
  } catch (error: any) {
    showErrorMessage(error);
    const key = `${action.payload.orgId || ''}:${action.payload.grainId || ''}:${action.payload.profileId || ''}:${action.payload.settingName || ''}`;
    yield put(UserAction.getUserInfoFields.failure({ key, error }));
  }
}

export function* getUserProfile(action: ReturnType<typeof UserAction.getUserProfile.request>): any {
  try {
    const isOnlineFromRedux: boolean = yield select(getNetworkStatus);
    const browserOnline = getBrowserOnline();
    const isOnline = isOnlineFromRedux && (!isElectron() || browserOnline !== false);
    let expiry = yield select(getExpiry);
    let expiryDate = expiry === undefined ? undefined : new Date(expiry);

    let currentDate = new Date();
    let cachedUserProfile: IUserProfile = yield select(getCachedUserProfile);
    const authenticatedUsername: string | undefined = yield call(getAuthenticatedUsername);
    const cacheMatchesAuthenticatedUser =
      !authenticatedUsername || cachedUserProfile?.username === authenticatedUsername;
    const shouldAttemptRemoteProfile =
      isOnline || (isElectron() && !!authenticatedUsername && !cacheMatchesAuthenticatedUser);
    yield call(logDesktopAuthEvent, 'getUserProfile.start', {
      isOnline,
      hasCachedProfile: cachedUserProfile !== undefined,
      hasCacheExpiry: expiryDate !== undefined,
      cacheUsername: cachedUserProfile?.username,
      authenticatedUsername,
      cacheMatchesAuthenticatedUser,
    });

    const canUseFreshCache =
      cachedUserProfile !== undefined
      && expiryDate !== undefined
      && expiryDate.getTime() > currentDate.getTime()
      && cacheMatchesAuthenticatedUser
      && (!isElectron() || !shouldAttemptRemoteProfile);

    if (canUseFreshCache) {
      console.log("Using cached user profile");
      yield call(logDesktopAuthEvent, 'getUserProfile.cached_fresh');
      yield put(UserAction.getUserProfile.success({
        cached: true,
        profile: cachedUserProfile,
        expiry: expiryDate
      }))
    } else {
      if (cachedUserProfile !== undefined
        && expiryDate !== undefined
        && expiryDate.getTime() > currentDate.getTime()
        && !cacheMatchesAuthenticatedUser) {
        yield call(logDesktopAuthEvent, 'getUserProfile.cached_user_mismatch', {
          cacheUsername: cachedUserProfile.username,
          authenticatedUsername,
        });
      }

      if (!shouldAttemptRemoteProfile) {
        if (cachedUserProfile !== undefined && cacheMatchesAuthenticatedUser) {
          console.log("Offline: using cached user profile even though cache expired");
          yield call(logDesktopAuthEvent, 'getUserProfile.cached_offline_expired');
          const offlineExpiry = new Date();
          offlineExpiry.setSeconds(offlineExpiry.getSeconds() + getCacheExpiryDuration());
          yield put(UserAction.getUserProfile.success({
            cached: true,
            profile: cachedUserProfile,
            expiry: offlineExpiry
          }));
        } else if (cachedUserProfile !== undefined && !cacheMatchesAuthenticatedUser) {
          throw new Error(`Internet connection required to fetch user profile for ${authenticatedUsername}`);
        } else {
          throw new Error('Internet connection required to fetch user profile');
        }
      } else {
      console.log("Fetching new user profile");
      yield call(logDesktopAuthEvent, 'getUserProfile.fetch_start');
      let newProfile = yield call(api.getUserProfile, action.payload);
      yield call(logDesktopAuthEvent, 'getUserProfile.fetch_success', {
        groupsCount: Array.isArray(newProfile?.groups) ? newProfile.groups.length : undefined,
        grainsCount: Array.isArray(newProfile?.grains) ? newProfile.grains.length : undefined,
      });

      let expiry = new Date();
      expiry.setSeconds(expiry.getSeconds() + getCacheExpiryDuration());

      yield put(UserAction.getUserProfile.success({
        cached: false,
        profile: newProfile,
        expiry: expiry
      }));
      cachedUserProfile = newProfile;
      }
    }

    yield call(updateGrainId, cachedUserProfile);
  } catch (error: any) {
    yield call(logDesktopAuthEvent, 'getUserProfile.failure', {
      errorName: error?.name || error?.code,
      errorMessage: error?.message,
    });
    showErrorMessage(error);
    yield put(UserAction.getUserProfile.failure(error));
  }
}

function* updateGrainId(cachedUserProfile: IUserProfile): Generator<any, void, any> {
  let initialGrainId = yield select(getGrainId);
  let grainId = initialGrainId;

  let isGrainIdAllowed = cachedUserProfile
    .grains
    .some((grain: IGrainWithProfiles) => grain.grainId === grainId);

  if (!isGrainIdAllowed) {
    grainId = cachedUserProfile.grains[0].grainId;
  }

  if (initialGrainId !== grainId) {
    yield put(GrainTypeAction.setGrainId(grainId));
  }
}

export function* updateUserSettings(action: ReturnType<typeof UserAction.updateUserSettings.request>): any {
  try {
    const response = yield call(api.updateUserSettings, action.payload);

    yield put(UserAction.updateUserSettings.success(response));
  } catch (error: any) {
    showErrorMessage(error);
    yield put(UserAction.updateUserSettings.failure(error));
  }
}

export function* updateUserLogo(action: ReturnType<typeof UserAction.updateUserLogo.request>): any {
  try {
    const response = yield call(api.updateUserLogo, action.payload);

    yield put(UserAction.updateUserLogo.success(response));
  } catch (error: any) {
    showErrorMessage(error);
    yield put(UserAction.updateUserLogo.failure(error));
  }
}

/*
 * WATCHERS
 */

export function* watchLogUserActivity() {
  yield takeEvery(UserAction.logUserActivity.request, logUserActivity);
}

export function* watchGetUserSettings() {
  yield takeEvery(UserAction.getUserSettings.request, getUserSettings);
}

export function* watchGetUserInfoFields() {
  yield takeEvery(UserAction.getUserInfoFields.request, getUserInfoFields);
}

export function* watchGetUserProfile() {
  yield takeEvery(UserAction.getUserProfile.request, getUserProfile);
}

export function* watchUpdateUserSettings() {
  yield takeEvery(UserAction.updateUserSettings.request, updateUserSettings);
}

export function* watchUpdateUserLogo() {
  yield takeEvery(UserAction.updateUserLogo.request, updateUserLogo);
}

export default function* root() {
  yield all([
    fork(watchLogUserActivity),
    fork(watchGetUserProfile),
    fork(watchGetUserSettings),
    fork(watchGetUserInfoFields),
    fork(watchUpdateUserLogo),
    fork(watchUpdateUserSettings)]);
}
